import { defineStore } from "pinia";
import type { PaginationAttributes } from "@/interface/pagination_interface";
import { ImportAssetRepository } from "@/repository/import_asset_repository";
import type { EquipmentDetails } from "@/interface/import/import_asset_interface";
import { useImportLocationStore } from "@/store/import/import_location_store";
import { useImportSearchIdsStore } from "@/store/import/import_id_store";
import { isAuthenticatedUser } from "@/utils/common";
import { appButtonClick } from "@/utils/analytics";

const locationStore = useImportLocationStore();
const searchIdsStore = useImportSearchIdsStore();

export const useImportAssetStore = defineStore("importAssetsStore", {
  state: (): {
    originalImportAsset: EquipmentDetails[];
    filteredImportAsset: EquipmentDetails[];
    selectedContainer: EquipmentDetails[];
    savedContainers: string[];
    noResultIds: string[];
    isLoading: boolean;
    error: string;
    bookingNumbers: string[];
    pagination: PaginationAttributes;
    bookingNumber: string;
    saveContainerLoading: boolean;
    containerSavedSuccessful: boolean;
    totalContainersInWatchlist: number;
  } => ({
    originalImportAsset: [] as EquipmentDetails[],
    filteredImportAsset: [] as EquipmentDetails[],
    selectedContainer: [] as EquipmentDetails[],
    savedContainers: [] as string[],
    noResultIds: [] as string[],
    isLoading: false,
    error: "" as string,
    bookingNumbers: [] as string[],
    pagination: {
      totalPages: 0,
      page: 1,
      pageSize: 25,
      totalRecords: 0
    },
    bookingNumber: "",
    saveContainerLoading: false,
    containerSavedSuccessful: false,
    totalContainersInWatchlist: 0
  }),
  persist: false,
  getters: {
    // get invalid ids
    getInvalidIds(): string[] {
      return this.noResult;
    }
  },
  actions: {
    async fetchImportAssets(): Promise<void> {
      this.resetStore();
      try {
        this.isLoading = true;
        const importRepository = new ImportAssetRepository();
        const importDetail = await importRepository.getImportAssetDetail();
        const isUserAuthenticated = isAuthenticatedUser();
        const existingWatchlistContainers = [];
        if (isUserAuthenticated) {
          const existingContainers: any = await importRepository.getExistingWatchListContainers();
          if (Object.keys(existingContainers).length && existingContainers.validIds.length > 0) {
            existingWatchlistContainers.push(...existingContainers.validIds);
          }
        }
        this.totalContainersInWatchlist = JSON.parse(JSON.stringify(existingWatchlistContainers.length));
        if (existingWatchlistContainers.length > 0) {
          const containerNumbersInWatchlist = new Set(existingWatchlistContainers.map((item) => item.containerNumber));
          importDetail.validIds = importDetail.validIds.map((item) => ({
            ...item,
            isAlreadyAdded: containerNumbersInWatchlist.has(item.containerNumber)
          }));
        }
        appButtonClick("Import Availability", "Search Results Count", locationStore.terminalSelected.oneStreamCode, {
          searchedContainerCount: searchIdsStore.importSearchIds.length,
          validContainersCount: importDetail.validIds.length,
          invalidContainersCount: importDetail.invalidIds.length,
          responseCode: "200",
          ...locationStore.anayticsTerminalDetails
        });
        // Keeping original list to filter data when needed
        this.originalImportAsset = importDetail.validIds;
        this.filteredImportAsset = importDetail.validIds;
        this.noResultIds = importDetail.invalidIds;
        // Booking numbers for filtering the table list
        this.bookingNumbers = importDetail.bols;
        this.updatePaginationCount();
        this.isLoading = false;
      } catch (e: any) {
        appButtonClick("Import Availability", "Search Results Count", locationStore.terminalSelected.oneStreamCode, {
          searchedContainerCount: searchIdsStore.importSearchIds.length,
          validContainersCount: 0,
          invalidContainersCount: 0,
          responseCode: e || "500",
          ...locationStore.anayticsTerminalDetails
        });
        this.isLoading = false;
        this.error = e;
      }
    },

    resetIntialState(): void {
      this.isLoading = false;
      this.error = "";
      this.saveContainerLoading = false;
      this.selectedContainer = [];
    },

    setInitialState(): void {
      const list = this.originalImportAsset;
      if (list.length > 0) {
        this.filteredImportAsset = list;
        this.updatePaginationCount();
      }
    },

    searchFilterImportAsset(searchText: string): void {
      this.filteredImportAsset = this.originalImportAsset.filter((asset: EquipmentDetails): boolean => {
        return (
          asset.containerNumber?.toLowerCase().includes(searchText) ||
          asset.billOfLadingNumber?.toLowerCase().includes(searchText) ||
          asset.vesselName?.toLowerCase().includes(searchText) ||
          asset.vesselOperator?.toLowerCase().includes(searchText)
        );
      });
      this.updatePaginationCount();
    },

    // Filter the import asset by booking number but should not change the original import asset list
    filterByBookingNumber(bookingNumber: string): void {
      const filteredImportAsset = this.originalImportAsset.filter((item: EquipmentDetails): boolean => {
        return item.billOfLadingNumber === bookingNumber;
      });
      this.filteredImportAsset = filteredImportAsset;
      this.updatePaginationCount();
    },

    // Add container to selected container list
    addContainerToSelectedContainerList(container: EquipmentDetails[]): void {
      this.selectedContainer = container;
    },

    // Remove booking number filter
    removeBookingNumberFilter(): void {
      this.bookingNumber = "";
      this.filteredImportAsset = [...this.originalImportAsset];
      this.updatePaginationCount();
    },

    // Reset the import asset list
    resetImportAssetList(): void {
      this.filteredImportAsset = [];
      this.updatePaginationCount();
    },

    // Set the booking number to bookingNumber
    setBookingNumber(bookingNumber: string): void {
      this.bookingNumber = bookingNumber;
    },

    // Clear invalid ids
    clearInvalidIds(): void {
      this.noResultIds = [];
    },

    // Resetting the pagination
    resetPagination(newPageSize = 25): void {
      this.pagination = {
        totalPages: 0,
        page: 1,
        pageSize: newPageSize,
        totalRecords: 0
      };
    },

    // Setting the page number
    setPageNumber(page: number): void {
      this.pagination.page = page;
    },

    // Updating the pagination count
    updatePaginationCount(): void {
      this.pagination = {
        ...this.pagination,
        totalPages: Math.ceil(this.filteredImportAsset.length / this.pagination.pageSize),
        totalRecords: this.filteredImportAsset.length,
        pageSize: 25,
        page: 1
      };
    },

    resetError(): void {
      this.error = "";
      this.noResultIds = [];
    },

    // Reset the store
    resetStore(): void {
      this.noResultIds = [];
      this.isLoading = false;
      this.error = "";
      this.bookingNumbers = [];
      this.pagination = {
        totalPages: 0,
        page: 1,
        pageSize: 25,
        totalRecords: 0
      };
      this.bookingNumber = "";
      this.selectedContainer = [];

      this.originalImportAsset = [];
      this.filteredImportAsset = [];
      this.saveContainerLoading = false;
      this.containerSavedSuccessful = false;
    },
    setLoading(val: boolean): void {
      this.isLoading = val;
    }
  }
});
